import React, { forwardRef } from 'react';

import type { AvatarGroupProps, AvatarProps } from '@mui/material';
import { alpha, Avatar, AvatarGroup } from '@mui/material';
import clsx from 'clsx';
import { Sparkle, UserRound } from 'lucide-react';
import { OrganizationIcon } from 'src/components/icons/OrganizationIcon';
import type { Apollo } from 'src/gql/generated';
import { appColors } from 'src/theme';
import type { UserSubset } from 'src/types';
import { makeStyles } from 'tss-react/mui';

const defaultOverlap = 6;

export const alineAvatarSizes = {
  lg: { fontSize: 16, size: 40 },
  md: { fontSize: 12, size: 32 },
  sm: { fontSize: 10, size: 24 },
  xs: { fontSize: 8, size: 20 },
  /** Matches the glyph box of an inline picker chip, so avatars don't grow the chip. */
  xxs: { fontSize: 8, size: 16 },
};

export type AlineAvatarSize = keyof typeof alineAvatarSizes;

/**
 * The '!important's are needed to override the default surplus avatar style when there are more
 * than 5 users. Do not remove them without testing that this surplus avatar is styled correctly.
 */
const useStyles = makeStyles<InternalStyleProps>()((theme, props) => {
  const { fontSize, size } = alineAvatarSizes[props.size ?? 'sm'];
  const placeholderColor = props.placeholderColor ?? theme.appColors.iconsType;
  const placeholderTint = alpha(placeholderColor, 0.14);
  return {
    /**
     * pxToRem has been removed from most of the styling but kept here because
     * it is used as part of a string with `!important`
     */
    avatar: {
      fontFamily: theme.typography.fontFamily,
      fontSize: theme.typography.pxToRem(fontSize) + ' !important',
      fontWeight: 'bold',
      height: theme.typography.pxToRem(size) + ' !important',
      opacity: props.opacity,
      width: theme.typography.pxToRem(size) + ' !important',
    },
    avatarInGroup: {
      '&:last-child': {
        marginLeft: 0,
      },
      border: '2px solid #fff',
      marginLeft: -1 * (props.overlap ?? defaultOverlap),
    },
    colorDefault: {
      backgroundColor: props.backgroundColor,
      color: theme.palette.common.white,
    },
    /**
     * A light tint of the role's color behind an icon in that color: softer than a filled user
     * avatar, so it reads as an open seat. The tint sits on white so it stays opaque where
     * avatars overlap, and `!important` wins over the `backgroundColor` callers pass for a role.
     */
    placeholder: {
      '& svg': { height: '58%', width: '58%' },
      backgroundColor: theme.palette.common.white + ' !important',
      backgroundImage: `linear-gradient(${placeholderTint}, ${placeholderTint})`,
      color: placeholderColor + ' !important',
    },
  };
});

const useAlineAiAvatarStyles = makeStyles()(theme => ({
  aiAvatar: {
    '& svg': {
      stroke: theme.appColors.primary,
    },
    backgroundColor: 'rgba(20, 110, 232, 0.12)',
    flexShrink: 0,
  },
}));

/**
 * Avatar can be passed a User in which case the avatar will use the initials of a User or email, whichever exists.
 * Or it can be passed a string, of which it shows the first character, uppercased: callers may
 * hand over a whole label such as a role name or a display name without trimming it first.
 * Or it can be passed a placeholder (a role, the workflow launcher): a seat no one fills yet,
 * drawn as a person icon on a light tint of the color the server stored on that seat, so two
 * seats with the same label still look like different records.
 * Or it can be passed an element via props.children which should be some sort of SVG Icon.
 */
type AvatarUserSubset = Pick<UserSubset, 'avatarColor' | 'displayName' | '__typename'>;
type AvatarOrgSubset = Pick<Apollo.Org, 'avatarColor' | 'name' | '__typename'>;
type AvatarGroupSubset = Pick<Apollo.Group, 'avatarColor' | 'id' | 'name' | '__typename'>;
interface AvatarPlaceholder {
  readonly __typename: 'AvatarPlaceholder';
  /** Null for a seat not saved yet, which takes the neutral tint. */
  readonly color: string | null;
  readonly label: string;
}

export type AvatarInfo =
  | AvatarUserSubset
  | AvatarOrgSubset
  | AvatarGroupSubset
  | AvatarPlaceholder
  | string
  | null;

export function toAvatarPlaceholder(label: string, color: string | null): AvatarPlaceholder {
  return { __typename: 'AvatarPlaceholder', color, label };
}

function avatarContent(
  avatarInfo: AlineAvatarProps['avatarInfo']
): React.ReactNode | string | null {
  if (!avatarInfo) return null;

  if (typeof avatarInfo === 'string') return avatarInfo.charAt(0).toUpperCase();

  switch (avatarInfo.__typename) {
    case 'Group':
      return avatarInfo.name.charAt(0).toUpperCase();
    case 'User':
      return avatarInfo.displayName.charAt(0).toUpperCase();
    case 'Org':
      return <OrganizationIcon />;
    case 'AvatarPlaceholder':
      return <UserRound aria-label={avatarInfo.label} role="img" strokeWidth={2.25} />;
    case undefined:
      return null;
  }
}

interface StyleProps {
  backgroundColor?: string;
  opacity?: number;
  overlap?: number;
  size?: AlineAvatarSize;
}

interface InternalStyleProps extends StyleProps {
  placeholderColor?: string;
}

interface AlineAvatarProps extends AvatarProps {
  avatarInfo?: AvatarInfo;
  styles?: StyleProps;
}

interface AlineAvatarGroupProps extends AvatarGroupProps {
  styles?: Omit<StyleProps, 'backgroundColor'>;
}

export function getAvatarColor(avatarInfo?: AvatarInfo): string | undefined {
  // Unassigned color is default MUI grey color.
  if (!avatarInfo) return;
  if (typeof avatarInfo === 'string') return appColors.iconsType;
  switch (avatarInfo.__typename) {
    case 'Group':
    case 'User':
    case 'Org':
      return avatarInfo.avatarColor;
    case 'AvatarPlaceholder':
      return;
  }
}

export const AlineAvatar = forwardRef(function AlineAvatar(
  props: AlineAvatarProps,
  ref: React.Ref<HTMLDivElement>
): JSX.Element {
  // NOTE: We destructure backgroundColor here so that it's not passed to the root DOM element, triggering a React warning.  Do NOT remove unused destructured variable.
  const { avatarInfo, children, className, styles = {}, ...rest } = props;
  /**
   * Callers pass `backgroundColor` from data that may not carry one (a typed-in email, a
   * role-only permission). Defaulting rather than spreading keeps an undefined override from
   * clobbering the color derived from `avatarInfo` and dropping the avatar to MUI's grey.
   */
  const { backgroundColor = getAvatarColor(avatarInfo), ...styleOverrides } = styles;
  const placeholder = getAvatarPlaceholder(avatarInfo);
  const { classes } = useStyles({
    backgroundColor,
    placeholderColor: placeholder?.color ?? undefined,
    ...styleOverrides,
  });
  const content = avatarContent(avatarInfo);
  const isPlaceholder = Boolean(placeholder);

  return (
    <Avatar
      ref={ref}
      className={clsx(classes.avatar, isPlaceholder && classes.placeholder, className)}
      classes={{ colorDefault: classes.colorDefault }}
      {...rest}
    >
      {content ? content : children}
    </Avatar>
  );
});

export const AlineAiAvatar = forwardRef(function AlineAiAvatar(
  props: Omit<AlineAvatarProps, 'avatarInfo'>,
  ref: React.Ref<HTMLDivElement>
): JSX.Element {
  const { children, className, ...rest } = props;
  const { classes } = useAlineAiAvatarStyles();

  return (
    <AlineAvatar
      ref={ref}
      avatarInfo={null}
      className={clsx(classes.aiAvatar, className)}
      {...rest}
    >
      {children ?? <Sparkle size={16} strokeWidth={2} />}
    </AlineAvatar>
  );
});

export const AlineAvatarGroup = forwardRef(function AlineAvatarGroup(
  props: AlineAvatarGroupProps,
  ref: React.Ref<HTMLDivElement>
): JSX.Element {
  const { children, className, styles = {}, ...rest } = props;
  const { classes } = useStyles(styles);

  return (
    <AvatarGroup
      ref={ref}
      classes={{ avatar: clsx(classes.avatar, classes.avatarInGroup) }}
      spacing={styles.overlap ?? defaultOverlap}
      {...rest}
    >
      {children}
    </AvatarGroup>
  );
});

function getAvatarPlaceholder(avatarInfo: AvatarInfo | undefined): AvatarPlaceholder | null {
  if (typeof avatarInfo !== 'object' || avatarInfo?.__typename !== 'AvatarPlaceholder') return null;
  return avatarInfo;
}
